import { useState, useCallback, useRef, useEffect } from 'react';
import { getScannedCode } from '@/utils/scannedCode';
import {
  useProcessArrivalScanMutation,
  ArrivalScanResponse,
} from '../services/arrivalScan.endpoints';
import { playSuccessSound, playErrorSound } from '../utils/scanSounds';

const OVERLAY_AUTO_CLOSE_MS = 1000;

interface UseArrivalScanProcessorProps {
  onSuccess: (result: ArrivalScanResponse) => void;
}

export const useArrivalScanProcessor = ({
  onSuccess,
}: UseArrivalScanProcessorProps) => {
  const [lastResult, setLastResult] = useState<ArrivalScanResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [failedShippingNumber, setFailedShippingNumber] = useState<number | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [processScan, { isLoading }] = useProcessArrivalScanMutation();

  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  const scheduleAutoClose = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    autoCloseTimerRef.current = setTimeout(() => {
      setShowOverlay(false);
      autoCloseTimerRef.current = null;
    }, OVERLAY_AUTO_CLOSE_MS);
  }, []);

  const handleScan = useCallback(
    async (scannedCode: string) => {
      const trimmedCode = scannedCode.trim();
      if (!trimmedCode || isLoading) return;

      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }

      setError(null);
      setLastResult(null);
      setFailedShippingNumber(null);
      setShowOverlay(false);

      const scanned = getScannedCode(trimmedCode);
      if (!scanned) {
        setError('Código inválido');
        playErrorSound();
        setShowOverlay(true);
        scheduleAutoClose();
        return;
      }
      const { code: shippingCode, mode } = scanned;

      try {
        const result = await processScan({ code: shippingCode, mode }).unwrap();

        if (result.transitionsApplied === 0) {
          const failureReason =
            result.transitions.find((t) => !t.success)?.reason ||
            'El pedido no cumple con las condiciones para arribar';
          setError(failureReason);
          setFailedShippingNumber(result.shippingNumber);
          playErrorSound();
        } else {
          setLastResult(result);
          onSuccess(result);
          playSuccessSound();
        }

        setShowOverlay(true);
        scheduleAutoClose();
      } catch (err: any) {
        const errorMessage =
          err?.data?.message ||
          err?.data?.error ||
          err?.message ||
          'Error de conexión con el servidor';
        setError(errorMessage);
        playErrorSound();
        setShowOverlay(true);
        scheduleAutoClose();
      }
    },
    [isLoading, processScan, onSuccess, scheduleAutoClose]
  );

  return {
    handleScan,
    isLoading,
    lastResult,
    error,
    failedShippingNumber,
    showOverlay,
    setShowOverlay,
  };
};
